'use client';

import { FormEvent, useMemo, useState } from 'react';

import {
    Button,
    ButtonList,
    ButtonType,
    ButtonVariant,
    Checkbox,
    Column,
    Columns,
    Divider,
    Icon,
    IconColor,
    IconName,
    IconSize,
    Input,
    InputAutoCompleteType,
    Modal,
    ModalBody,
    ModalFooter,
    ModalSize,
    RadioTile,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TypographyAlign,
    TypographyColor,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';
import { interestService } from '@services/interest';

import { setInputProps } from '@helpers/input';
import { noop } from '@helpers/try-catch';

import { ERROR_CONTEXT, PATTERNS } from '@constants/provider';

import {
    EligibilityResults,
    JourneyConfigType,
    sendForecastTracking,
    sendTrackingFromEligibilityMismatchError,
} from '@eligibility/helpers';
import { INTEREST_TECHNOLOGY } from '@models/interest';
import { z } from 'zod';

import { ImageCtaBox } from './image-cta-box';

type ForecastProps = {
    results: EligibilityResults;
    journeyConfig: JourneyConfigType;
};

export function Forecast({ results, journeyConfig }: Readonly<ForecastProps>) {
    const shouldDisplay: boolean = useMemo(() => {
        const hasOffers = Object.values(results.offers).flat().length > 0;
        const isFtth = hasOffers ? results.technology === 'FTTH' : false;
        return results.error !== ERROR_CONTEXT.ERROR && !isFtth && !journeyConfig.hideForecast;
    }, [results]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleModalOpen = () => {
        setIsModalOpen(true);
        sendTrackingFromEligibilityMismatchError(journeyConfig, 'Me_prevenir_pour_la_fibre');
    };

    if (!shouldDisplay) {
        return null;
    }

    const appConfig = getAppConfig();
    return (
        <ImageCtaBox
            backgroundImage={`${appConfig.assets.cmsImages.url}/original/image/CMS/ecommerce/eligibility/prise-interet-fibre.webp`}
            title='On vous prévient dès que la fibre est disponible ⏰'
            text={
                <Text level={TextLevels.ONE}>
                    Vous nous <strong>laissez vos coordonnées</strong>, on vous appelle dès que la fibre arrive chez
                    vous&nbsp;!
                </Text>
            }
            cta={
                <Button className='' variant={ButtonVariant.PRIMARY} fullwidth onClick={handleModalOpen}>
                    Me prévenir
                </Button>
            }
        >
            {isModalOpen && (
                <ForecastModal onClose={() => setIsModalOpen(false)} journeyConfig={journeyConfig} results={results} />
            )}
        </ImageCtaBox>
    );
}

const FormSchema = z
    .object({
        email: z.email({ error: 'Doit être une adresse email valide' }),
        firstName: z.string().min(1).regex(PATTERNS.NAME, 'Contient des caractères invalides'),
        lastName: z.string().min(1).regex(PATTERNS.NAME, 'Contient des caractères invalides'),
        phone: z.string().min(1).regex(PATTERNS.PHONE, 'Doit respecter le format 0XXXXXXXXX'),
        optinRelocation: z.enum(['0', '1'], { error: "'Veuillez répondre à la question'" }),
        optin: z.enum(['true', 'false']),
        optinSmsMail: z.enum(['true', 'false']),
    })
    .refine((data) => data.optin === 'true' || data.optinSmsMail === 'true', {
        message: 'Choisissez au moins un moyen de contact',
        path: ['optin', 'optinSmsMail'],
    });

interface ForecastModalProps {
    onClose?: () => void;
    journeyConfig: JourneyConfigType;
    results: EligibilityResults;
}

function ForecastModal({ onClose, journeyConfig, results }: Readonly<ForecastModalProps>) {
    const [isClientInformed, setIsClientInformed] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        optinRelocation: '0',
        optin: 'true',
        optinSmsMail: 'false',
    });
    const [formError, setFormError] = useState<Record<string, string> | null | undefined>(undefined);

    const handleInputChange = (key: string, value: string) => {
        const newFormData = {
            ...formData,
            [key]: value,
        };

        setFormData(newFormData);

        FormSchema.parseAsync({
            ...formData,
            [key]: value,
        })
            .then(() => {
                setFormError(null);
            })
            .catch((error) => {
                if (error instanceof z.ZodError) {
                    const errors = error.issues.reduce(
                        (acc, issue) => {
                            const key = String(issue.path[0]);
                            if (newFormData[key as keyof typeof formData]) {
                                acc[key] = issue.message;
                            }
                            return acc;
                        },
                        {} as Record<string, string>,
                    );
                    setFormError(errors);
                }
            });
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        interestService
            .create(
                {
                    person: {
                        email: formData.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phone: formData.phone,
                    },
                    address: {
                        city: results.address.commune,
                        postalCode: results.address.codePostal,
                        streetNumber: results.address.numeroVoie,
                        street: results.address.voie,
                        extension: results.address.extension,
                    },
                    choices: {
                        technology: INTEREST_TECHNOLOGY.FTTH,
                        isRelocation: formData.optinRelocation === '1',
                        authorizeCall: formData.optin === 'true',
                        authorizeSmsOrEmail: formData.optinSmsMail === 'true',
                    },
                },
                results.user,
            )
            .catch(noop)
            .finally(() => {
                setIsClientInformed(true);
                sendForecastTracking(journeyConfig.tracking);
            });
    };

    return (
        <Modal
            active
            size={ModalSize.SMALL}
            onClose={onClose}
            title={
                isClientInformed
                    ? 'On vous contacte dès que la fibre arrive chez vous\u00A0!'
                    : "Être prévenu de l'arrivée de la fibre"
            }
        >
            {isClientInformed ? (
                <>
                    <ModalBody>
                        <Text level={TextLevels.ONE} typo={TypographyAlign.TEXT_CENTERED}>
                            <Icon name={IconName.CHECK_CIRCLE} color={IconColor.SUCCESS} size={IconSize.HUGE} />
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonList>
                            <Button
                                type={ButtonType.BUTTON}
                                variant={ButtonVariant.PRIMARY}
                                accessibilityLabel='Fermer le modal'
                                onClick={onClose}
                            >
                                Retour
                            </Button>
                        </ButtonList>
                    </ModalFooter>
                </>
            ) : (
                <form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Text typo={TypographyAlign.TEXT_LEFT}>
                            Laissez-nous vos coordonnées, on vous préviendra dès que la fibre arrive à cette
                            adresse&nbsp;!
                        </Text>
                        <Divider />
                        <Input
                            name='email'
                            placeholder='Email'
                            accessibilityLabel='Email'
                            autoCompleteType={InputAutoCompleteType.EMAIL}
                            onChange={(event) => handleInputChange(event.inputName, event.inputValue)}
                            {...setInputProps({ error: formError?.['email'] })}
                        />
                        <Input
                            name='firstName'
                            placeholder='Prénom'
                            accessibilityLabel='Prénom'
                            onChange={(event) => handleInputChange(event.inputName, event.inputValue)}
                            {...setInputProps({ error: formError?.['firstName'] })}
                        />
                        <Input
                            name='lastName'
                            placeholder='Nom'
                            accessibilityLabel='Nom'
                            onChange={(event) => handleInputChange(event.inputName, event.inputValue)}
                            {...setInputProps({ error: formError?.['lastName'] })}
                        />
                        <Input
                            name='phone'
                            placeholder='Téléphone'
                            accessibilityLabel='Téléphone'
                            autoCompleteType={InputAutoCompleteType.TEL}
                            onChange={(event) => handleInputChange(event.inputName, event.inputValue)}
                            {...setInputProps({ error: formError?.['phone'] })}
                        />
                        <Spacer size={SpacerSize.SIX} />
                        <Text level={TextLevels.ONE} typo={TypographyAlign.TEXT_LEFT}>
                            Allez-vous déménager prochainement&nbsp;?
                        </Text>
                        <Columns mobile>
                            <Column narrow>
                                <RadioTile
                                    id='optinRelocationYes'
                                    label='Oui'
                                    name='optinRelocation'
                                    value='1'
                                    accessibilityLabel='Oui, je déménage'
                                    onChange={(event) => handleInputChange(event.radioName, event.radioValue)}
                                    checked={formData.optinRelocation === '1'}
                                    horizontal
                                    className='is-narrow'
                                />
                            </Column>
                            <Column narrow>
                                <RadioTile
                                    id='optinRelocationNo'
                                    label='Non'
                                    name='optinRelocation'
                                    value='0'
                                    accessibilityLabel='Non, je ne déménage pas'
                                    onChange={(event) => handleInputChange(event.radioName, '0')}
                                    checked={formData.optinRelocation === '0'}
                                    horizontal
                                    className='is-narrow'
                                />
                            </Column>
                        </Columns>
                        <Text level={TextLevels.ONE} typo={TypographyAlign.TEXT_LEFT}>
                            Vous acceptez le traitement de vos données personnelles (nom, adresse email, numero de
                            téléphone) par Bouygues Telecom pour vous adresser des informations sur notre Offre fibre.
                        </Text>
                        <Columns>
                            <Column>
                                <Checkbox
                                    name='optin'
                                    value='true'
                                    label='Oui, par téléphone'
                                    accessibilityLabel='Me contacter par téléphone'
                                    onChange={(event) =>
                                        handleInputChange(event.checkboxName, event.checkboxChecked.toString())
                                    }
                                    checked={formData.optin === 'true'}
                                />
                            </Column>
                            <Column>
                                <Checkbox
                                    name='optinSmsMail'
                                    value='true'
                                    label='Oui, par SMS & email'
                                    accessibilityLabel='Me contacter par SMS et Email'
                                    onChange={(event) =>
                                        handleInputChange(event.checkboxName, event.checkboxChecked.toString())
                                    }
                                    checked={formData.optinSmsMail === 'true'}
                                />
                            </Column>
                        </Columns>
                        {formError?.['optin'] && (
                            <>
                                <Text
                                    level={TextLevels.THREE}
                                    typo={[TypographyAlign.TEXT_LEFT, TypographyColor.TEXT_ERROR]}
                                    marginless
                                >
                                    {formError['optin']}
                                </Text>
                                <Spacer size={SpacerSize.SIX} />
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <ButtonList>
                            <Button
                                type={ButtonType.SUBMIT}
                                variant={ButtonVariant.CONVERSION}
                                disabled={formError !== null}
                            >
                                Être prévenu
                            </Button>
                            <Button type={ButtonType.BUTTON} variant={ButtonVariant.SECONDARY} onClick={onClose}>
                                Annuler
                            </Button>
                        </ButtonList>
                    </ModalFooter>
                </form>
            )}
        </Modal>
    );
}
